import React, {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CircularProgress} from '@mui/material';
import {ProfConfig} from "../../../../api/config/AuthConfig";
import {AuthContext} from "../../../../context/AuthContext";
import {getRequest, postRequest} from "../../../../api/postRequest";
import {NahrainButton} from "../../../Common/component/NahrainButton";

export const ExamSelectStudentsDialogSidebar = ({onClickNext, examInfo}) => {
    const [t] = useTranslation("global");
    const [, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const {accessToken} = useContext(AuthContext);

    const initialState = {
        students: {
            data: [],
            loading: true,
            totalUsers: 0,
            pageSize: 1,
            currentPage: 1,
        },
        selectedIds: [],
        searchQuery: ""
    };

    const [studentsData, setStudentsData] = useState(initialState);
    const hasFetched = useRef(false);

    const fetchStudents = () => {
        setIsLoading(true);
        getRequest({
            path: ProfConfig.GET_ALL_CURRICULUM_STUDENTS(examInfo.curriculumId),
            onSuccess: (data) => updateStudentsData(data),
            onError: (err) => {
                console.error(err);
                setIsLoading(false);
            },
            token: accessToken,
        });
    };

    const updateStudentsData = (data) => {
        const students = data.payload.map((student) => ({
            fullName: student.fullName,
            email: student.email,
            id: student.id,
            date: student.date,
            stageType: student.stageType,
        }));

        setStudentsData((prevState) => ({
            ...prevState,
            students: {
                data: students,
                loading: false,
                totalUsers: students.length,
                pageSize: 1,
            },
        }));
        setIsLoading(false);
    };

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchStudents();
    }, [examInfo.curriculumId]);

    const updateStudentSelection = (id, isSelected) => {
        setStudentsData((prevState) => {
            const updatedStudents = prevState.students.data.map((student) =>
                student.id === id ? {...student, isSelected} : student
            );

            const updatedSelectedIds = isSelected
                ? [...prevState.selectedIds, id]
                : prevState.selectedIds.filter(selectedId => selectedId !== id);

            return {
                ...prevState,
                students: {
                    ...prevState.students,
                    data: updatedStudents,
                },
                selectedIds: updatedSelectedIds,
            };
        });
    };

    const toggleSelectAll = () => {
        setStudentsData((prevState) => {
            const allSelected = prevState.selectedIds.length === prevState.students.data.length;
            const updatedStudents = prevState.students.data.map(student => ({
                ...student,
                isSelected: !allSelected
            }));

            return {
                ...prevState,
                students: {
                    ...prevState.students,
                    data: updatedStudents,
                },
                selectedIds: !allSelected
                    ? updatedStudents.map(student => student.id)
                    : [],
            };
        });
    };

    const handleCreateExam = () => {
        setIsCreating(true);

        const examData = {
            ...examInfo,
            studentsIds: studentsData.selectedIds
        };
        console.log(examData)

        postRequest({
            path: ProfConfig.EXAM,
            data: examData,
            token: accessToken,
            onSuccess: () => {
                onClickNext();
                setIsCreating(false);
            },
            onError: (err) => {
                console.error(err);
                setIsCreating(false);
            }
        });
    };

    return (
        <div className={`max-w-[700px] flex flex-col h-full justify-between mx-auto relative`}>
            <div>
                <div className={`mb-4`}>
                    <h1 className={`text-onBackground text-2xl font-semibold`}>
                        {t("select_students_for_exam")}
                    </h1>
                    <p className={`text-onBackgroundCaption text-[16px] mt-2`}>
                        {t("select_students_for_exam_description")}
                    </p>

                    <button
                        onClick={toggleSelectAll}
                        className="mt-4 px-4 py-2 bg-primary text-onPrimary rounded-lg"
                    >
                        {studentsData.selectedIds.length === studentsData.students.data.length
                            ? t("deselect_all")
                            : t("select_all")}
                    </button>
                </div>

                {studentsData.students.loading ? (
                    <div className="flex justify-center">
                        <CircularProgress sx={{color: "rgba(var(--on-background))"}}/>
                    </div>
                ) : studentsData.students.data.length > 0 ? (
                    studentsData.students.data.map((student) => (
                        <StudentSelectableCard
                            key={student.id}
                            name={student.fullName}
                            email={student.email}
                            stage={t(student.stageType.toLowerCase())}
                            selected={studentsData.selectedIds.includes(student.id)}
                            onClick={() => updateStudentSelection(
                                student.id,
                                !studentsData.selectedIds.includes(student.id)
                            )}
                        />
                    ))
                ) : (
                    <h1 className={`text-onBackground font-semibold border p-2 rounded-lg border-softGray`}>
                        {t("no_students_available")}
                    </h1>
                )}
            </div>

            <div className={`sticky bg-card w-full bottom-0 py-4 mt-2 items-end`}>
                <NahrainButton
                    onClick={handleCreateExam}
                    className={`w-full`}
                    isLoading={isCreating}
                    disabled={isCreating || studentsData.selectedIds.length === 0}
                >
                    <p className="text-2xl font-semibold">{t("create_new_exam")}</p>
                </NahrainButton>
            </div>
        </div>
    );
};

const StudentSelectableCard = ({name, email, stage, selected, onClick}) => {
    return (
        <div
            onClick={onClick}
            className={`cursor-pointer flex flex-row gap-2 select-none items-center justify-between border-2 ${
                selected ? "border-primary" : "border-softGray"
            } mt-2 p-3 rounded-xl shadow-sm w-full`}
        >
            <div className="flex flex-row items-center gap-3 py-2">
                <SelectionCheckbox selected={selected}/>
                <div>
                    <h2 className="font-bold text-xl text-onBackground">
                        {name}
                    </h2>
                    <p className="text-onBackgroundCaption">{email}</p>
                    <p className="text-onBackgroundCaption">{stage}</p>
                </div>
            </div>
        </div>
    );
};

const SelectionCheckbox = ({selected}) => {
    return (
        <div
            className={`w-6 h-6 flex items-center justify-center border-2 rounded-md cursor-pointer transition 
                ${selected ? "bg-primary border-primary" : "border-gray-400"}`}
        >
            {selected && (
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                </svg>
            )}
        </div>
    );
};